import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { batch, connect } from "react-redux";
import { throttle } from "lodash";
import { StyledSoundAddBlock, StyledTimelineContainer } from "./styled";
import Seeker from "./Seeker";
import { TimelineContext } from "../../_utils/timeline";
import { DURATION_PIXEL_RATIO } from "../../_components/styled/variable";
import SoundBlock from "./SoundBlock";
import Rail from "./Rail";
import { setActivePage } from "../../_actions/textToolbarActions";
import Ruler from "./Ruler";
import {
  wsAddPage,
  wsRemoveMusic,
  wsUpdateAnimation,
  wsUpdateMusic,
} from "../../_actions/webSocketAction";
import {
  closeAdvancedSettings,
  closeSlider,
  openAdvancedSettings,
  openSlider,
  switchMediaTab,
} from "../../_actions/sidebarSliderActions";
import {
  ADVANCED_ANIMATION_PANEL,
  ADVANCED_EDITOR_EDIT,
  MUSIC_SLIDER_PANEL,
} from "../../_components/details/constants";
import { openBottomPanel } from "../../_actions/bottomPanelActions";
import { BottomPanelViewTypes } from "../../_utils/constants";
import VideoRail from "./VideoRail";
import dayjs from "dayjs";
import { FaPlus } from "react-icons/fa";
import { checkArtboardLimit } from "../../_utils/common";
import { useTranslation } from "react-i18next";
import { Box, Flex, Icon } from "@chakra-ui/react";

export const TimelineComponent = ({
  setActivePage,
  activePage,
  wsAddPage,
  story,
  wsRemoveMusic,
  wsUpdateMusic,
  switchMediaTab,
  openBottomPanel,
  wsUpdateAnimation,
  openAdvancedSettings,
  closeAdvancedSettings,
  closeSlider,
  selectedArtboard,
  pagestore,
  subscription,
  canvasRef,
}) => {
  const { t } = useTranslation(["studio"]);
  const {
    duration,
    pagesData,
    audioData,
    currentTime,
    seek,
    updateStoryAudioState,
    timescale,
    updateArtboardDuration,
  } = useContext(TimelineContext);

  const [extraBufferSeconds, setExtraBufferSeconds] = useState(30);


  const currentPos = currentTime * DURATION_PIXEL_RATIO * timescale;
  const seekerRef = useRef(null);
  const seekerTrack = useRef(null);
  const timelineContainer = useRef(null);

  // On change the position of the seeker
  const onChangePosition = (e, d) => {
    const seekDurationInSeconds = d.x / (DURATION_PIXEL_RATIO * timescale);
    seek(seekDurationInSeconds);
  };

  // On click artboard block
  const switchArtboardOnClick = (pageId, pageIdx) => {
    setActivePage(pageId, pageIdx, true);
  };

  // Callback on addArtboad
  const addArtboard = () => {
    const isAvailable = checkArtboardLimit(pagestore, subscription);
    if (isAvailable) wsAddPage();
  };

  // Callback on clicking add music
  const addMusic = () => {
    batch(() => {
      switchMediaTab(MUSIC_SLIDER_PANEL);
    });
  };

  // When Story music is Dragged
  const onSubmitStoryMusicOffset = (newOffset) => {
    const musicData = story?.payload?.payload?.music;
    if (!musicData) return;
    // If delta is too small to adjust (Ignore 10ms delta)
    if (Math.abs((musicData.offset ?? 0) - newOffset) < 0.01) return;

    updateStoryAudioState({ offset: newOffset });

    wsUpdateMusic({
      music: { offset: newOffset },
    });
  };

  // When story music is trimmed
  const onSubmitStoryMusicTrim = (startTime, endTime, offset) => {
    const musicData = story?.payload?.payload?.music;
    if (!musicData) return;
    updateStoryAudioState({
      start: startTime,
      end: endTime,
      offset,
    });
    wsUpdateMusic({
      music: {
        start: startTime,
        end: endTime,
        offset,
      },
    });
  };

  // On delete music
  const onDeleteMusic = () => {
    wsRemoveMusic();
  };

  // On adjust music open expanded view
  const onAdjustMusic = () => {
    batch(() => {
      closeAdvancedSettings();
      closeSlider();
      openBottomPanel(BottomPanelViewTypes.TRIM_STORY_MUSIC);
    });
  };

  const intervalSeconds = useMemo(() => {
    return Math.ceil(2 / timescale);
  }, [timescale]);

  const followSeeker = () => {
    if (
      seekerRef.current?.innerSeekerRef?.current &&
      timelineContainer.current
    ) {
      const innerSeekerRef = seekerRef.current.innerSeekerRef.current;
      const viewContainerWidth = timelineContainer.current.offsetWidth;
      const seekerPos = innerSeekerRef.getBoundingClientRect().left;
      const seekerTrackPos = seekerTrack.current.getBoundingClientRect().left;
      const offsetPosition = seekerPos - seekerTrackPos;

      timelineContainer.current.scroll({
        left:
          offsetPosition - viewContainerWidth / 2 > 0
            ? offsetPosition - viewContainerWidth / 2
            : 0,
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const followRndDragHandler = (refElement, direction = "right") => {
    if (!refElement || !timelineContainer.current || !seekerTrack.current)
      return;

    const viewContainerWidth = timelineContainer.current.offsetWidth;
    const viewContainerPos =
      timelineContainer.current.getBoundingClientRect().left;
    const handlerPos =
      refElement.getBoundingClientRect()[
        direction === "right" ? "right" : "left"
      ];
    const seekerTrackPos = seekerTrack.current.getBoundingClientRect().left;
    const offsetPosition = handlerPos - seekerTrackPos;

    const dragPositionFraction =
      (handlerPos - viewContainerPos) / viewContainerWidth;

    if (dragPositionFraction <= 0.2) {
      timelineContainer.current.scroll({
        left: offsetPosition - viewContainerWidth * 0.2,
        top: 0,
        behavior: "smooth",
      });
    }

    if (dragPositionFraction >= 0.8) {
      timelineContainer.current.scroll({
        left: offsetPosition - viewContainerWidth * 0.8,
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledScrollFollowerToSeeker = useCallback(
    throttle(followSeeker, 2000),
    []
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledScrollFollowerToSeeker500 = useCallback(
    throttle(followSeeker, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledFollowDragHandler = useCallback(
    throttle(followRndDragHandler, 50),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    throttledScrollFollowerToSeeker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime]);

  return (
    <StyledTimelineContainer
      id="timeline-container"
      ref={timelineContainer}
      onScroll={(e) => {
        if (
          e.target.scrollWidth - e.target.scrollLeft <=
          e.target.offsetWidth
        ) {
          setExtraBufferSeconds(extraBufferSeconds + 5);
        }
      }}
    >
      <Box pl={4}>
        <div
          id="seeker-track"
          ref={seekerTrack}
          style={{ width: DURATION_PIXEL_RATIO * timescale * duration }}
        >
          <Seeker
            pos={currentPos}
            parentId="#seeker-track"
            onChangePosition={onChangePosition}
            ref={seekerRef}
            onDrag={() => {
              throttledScrollFollowerToSeeker500();
            }}
          />
        </div>
        <Ruler
          id="timeline-ruler"
          duration={duration}
          scale={timescale}
          onSeek={seek}
          backgroundColor={"transparent"}
          style={{
            width: "100%",
            height: 30,
            cursor: "pointer",
          }}
          longLineSize={8}
          shortLineSize={4}
          segment={5}
          unit={intervalSeconds}
          onClick={(s) => seek(Math.min(s, duration))}
          textAlign={"center"}
          textFormat={(v) => {
            if (duration > 30) {
              return dayjs.duration(v, "seconds").format("mm:ss");
            } else {
              return `${Number(v).toFixed(1)}s`;
            }
          }}
          showFirstTextLeftAligned
        />
        <VideoRail
          canvasRef={canvasRef}
          duration={duration}
          timescale={timescale}
          pagesData={pagesData}
          activePage={activePage}
          switchArtboardOnClick={switchArtboardOnClick}
          seek={seek}
          openAdvancedSettingsArtboard={(pageId, pageIdx) => {
            setActivePage(pageId, pageIdx, true);
            openAdvancedSettings(
              ADVANCED_ANIMATION_PANEL,
              ADVANCED_EDITOR_EDIT
            );
          }}
          updateArtboardDuration={(index, duration) => {
            const page = pagesData[index];
            updateArtboardDuration(index, duration);
            wsUpdateAnimation({
              page: page.id,
              payload: {
                ...page.payload,
                animation: {
                  ...page.payload.animation,
                  duration: duration,
                },
              },
            });
          }}
          addArtboard={addArtboard}
          selectedArtboard={selectedArtboard}
          extraBufferSeconds={extraBufferSeconds}
          onResizeArtboardBlock={(
            idx,
            dragEvent,
            direction,
            ref,
          ) => {
            throttledFollowDragHandler(ref, direction);
          }}
        />
        <Rail
          id="audio-rail"
          height="48px"
          width={duration * DURATION_PIXEL_RATIO * timescale}
        >
          {audioData ? (
            <SoundBlock
              parentId="#audio-rail"
              duration={audioData.duration}
              offset={audioData.offset}
              title={audioData.title}
              start={audioData.start}
              end={audioData.end}
              onChangeOffset={onSubmitStoryMusicOffset}
              onChangeStartEndTime={onSubmitStoryMusicTrim}
              onDeleteMusic={onDeleteMusic}
              onAdjustMusic={onAdjustMusic}
              scale={timescale}
            ></SoundBlock>
          ) : (
            <Flex alignSelf="stretch" px={2} width="100%">
              <StyledSoundAddBlock onClick={addMusic}>
                <Icon as={FaPlus} mr={2} />
                {t("add_music")}
              </StyledSoundAddBlock>
            </Flex>
          )}
        </Rail>
      </Box>
    </StyledTimelineContainer>
  );
};

TimelineComponent.propTypes = {};

const mapStateToProps = (state) => ({
  activePage: state.editor.activePage,
  story: state.story,
  pages: state.pagestore?.pages,
  pagestore: state.pagestore,
  selectedArtboard: state.motionStore?.selectedArtboard,
  subscription: state.subscription,
});

const mapDispatchToProps = (dispatch) => ({
  closeSlider: () => dispatch(closeSlider()),
  closeAdvancedSettings: () => dispatch(closeAdvancedSettings()),
  setActivePage: (pageId, pageIdx, isSelected) =>
    dispatch(setActivePage(pageId, pageIdx, isSelected)),
  wsAddPage: () => dispatch(wsAddPage()),
  wsRemoveMusic: (payload) => dispatch(wsRemoveMusic(payload)),
  wsUpdateMusic: (payload) => dispatch(wsUpdateMusic(payload)),
  switchMediaTab: (tab) => dispatch(switchMediaTab(tab)),
  openSlider: (panelType) => dispatch(openSlider(panelType)),
  openBottomPanel: (panelType) => dispatch(openBottomPanel(panelType)),
  wsUpdateAnimation: (payload) => dispatch(wsUpdateAnimation(payload)),
  openAdvancedSettings: (panelName, selectedTab) =>
    dispatch(openAdvancedSettings(panelName, selectedTab, null, false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimelineComponent);
